/**
 * MODULAR API CONFIGURATION
 * =========================
 * Single place to connect the frontend to your backends.
 *
 * 1. Tenet API  -> your deployed fastapi_app.py (Railway, Render, ...)
 * 2. Financify SaaS API -> OTP login, usage counter, plan checks
 *
 * Local development defaults to the real Tenet API at http://127.0.0.1:8000
 * and the existing Financify SaaS authentication backend. To connect production, either:
 *   - set REACT_APP_HONEY_API_URL / REACT_APP_FINANCIFY_AUTH_URL in frontend/.env, or
 *   - edit the values below.
 *
 * Endpoint paths are also configurable to match your production route names.
 */

const FALLBACK_HONEY_API = process.env.REACT_APP_BACKEND_URL || "http://127.0.0.1:8000";
const FALLBACK_AUTH_API = "https://financify-saas.onrender.com";
const FALLBACK_PRICING_URL = "/#/pricing";

export const API_CONFIG = {
  /** Base URL of the Tenet API (fastapi_app.py). No trailing slash. */
  honeyApiBase: (process.env.REACT_APP_HONEY_API_URL || FALLBACK_HONEY_API).replace(/\/$/, ""),

  /** Base URL of the Financify SaaS backend (OTP + usage). No trailing slash. */
  authApiBase: (process.env.REACT_APP_FINANCIFY_AUTH_URL || FALLBACK_AUTH_API).replace(/\/$/, ""),

  /** Tenet API endpoint paths (match fastapi_app.py). */
  honey: {
    health: "/health",
    scan: "/scan",
    scanTicker: (ticker: string) => `/scan/${encodeURIComponent(ticker)}`,
    scanUpload: "/scan/upload",
    manualApply: "/manual/apply",
    peerUsage: "/usage/peer-comparison",
    reportPdf: "/report.pdf",
    convictionChecklistPdf: "/report/conviction-checklist.pdf",
    manualTemplate: "/manual/template.csv",
    professorBeeAsk: "/professor-bee/ask",
  },

  /** Financify SaaS endpoint paths — adjust to your production routes. */
  auth: {
    requestOtp: "/auth/request-otp",
    verifyOtp: "/auth/verify-otp",
    logout: "/auth/logout",
    logoutAll: "/auth/logout-all",
    usageCheck: "/usage/check",
    usageRecord: "/usage/record",
    policyAccept: "/policy/accept",
    checkoutIntents: "/checkout/intents",
    paymentStatus: (intentId: string) => `/payments/status/${encodeURIComponent(intentId)}`,
    paymentHistory: "/payments/history",
    adminDashboard: "/admin/dashboard",
    journalChecklist: "/journal/checklist",
    journalChecklistEntry: (entryId: string) => `/journal/checklist/${encodeURIComponent(entryId)}`,
    watchlistSnapshot: "/watchlist/snapshot",
    watchlistSnapshotById: (snapshotId: string) => `/watchlist/snapshot/${encodeURIComponent(snapshotId)}`,
    trackRecordSummary: "/track-record/summary",
    myTrackRecordSummary: "/track-record/my-summary",
    streak: "/streak",
  },

  /** Tool name registered in the Financify usage system. */
  toolName: "honey-scanner-production",

  pricingUrl: FALLBACK_PRICING_URL,
  checkout: {
    starter: process.env.REACT_APP_STARTER_CHECKOUT_URL || "",
    standard: process.env.REACT_APP_STANDARD_CHECKOUT_URL || "",
    pro: process.env.REACT_APP_PRO_CHECKOUT_URL || process.env.REACT_APP_SURECART_CHECKOUT_URL || "",
    extra: process.env.REACT_APP_EXTRA_SCANS_CHECKOUT_URL || "",
  },

  /**
   * SureCart's own customer self-service billing portal (manage payment
   * method, view invoices, cancel a subscription). Set
   * REACT_APP_SURECART_PORTAL_URL to your SureCart account's portal URL -
   * this app has no server-side cancellation API integration, so this link
   * is the only way a subscriber can cancel.
   */
  manageSubscriptionUrl: process.env.REACT_APP_SURECART_PORTAL_URL || "",
};

export const SESSION_STORAGE_KEY = "financify_hs_session";
export const PAYMENT_INTENT_STORAGE_KEY = "financify_hs_payment_intent";
